<template>
  <div class="mt-16 mb-16 pl-8 pr-4 my-0 mx-auto max-w-xl">
    <h2 class="md:text-center uppercase text-3xl">Регистрация</h2>
    <p class="md:text-center py-5">
      Для подключения Вас к бесплатной услуге «Консьерж-сервис» заполните, пожалуйста, анкету участника:
    </p>

    <form class="w-full" @submit.prevent="submit">
      <div class="flex flex-col">
        <Input
          v-model="form.last_name"
          v-model:error="errors.last_name"
          name="lname"
          autocomplete="family-name"
          required
          placeholder="Фамилия*"
          class="col-span-2 sm:col-span-1"
        />
        <Input
          v-model="form.first_name"
          v-model:error="errors.first_name"
          placeholder="Имя"
          name="fname"
          autocomplete="given-name"
          class="col-span-2 sm:col-span-1"
        />
        <Input
          v-model="form.middle_name"
          v-model:error="errors.middle_name"
          placeholder="Отчество"
          name="mname"
          autocomplete="additional-name"
          class="col-span-2 sm:col-span-1"
        />
        <DatePicker
          v-model="form.birthday"
          v-model:error="errors.birthday"
          placeholder="Дата рождения*"
          name="bday"
          autocomplete="bday"
          required
          class="col-span-2 sm:col-span-1"
        />
        <PhoneInput
          v-model="form.phone"
          v-model:error="errors.phone"
          placeholder="Номер телефона"
          :disabled="needConfirm && !!smsTimeRemaining && !isEscort"
          name="phone"
          autocomplete="tel"
          required
          class="col-span-2 sm:col-span-1"
        />
        <Input
          v-model="form.email"
          v-model:error="errors.email"
          type="email"
          placeholder="Электронная почта*"
          name="email"
          autocomplete="email"
          required
          class="col-span-2 sm:col-span-1"
        />
        <Input
          v-model="form.train_number"
          v-model:error="errors.train_number"
          placeholder="Номер поезда*"
          name="train_number"
          required
          class="col-span-2 sm:col-span-1"
        />
      </div>

      <Checkbox v-model="form.accept" v-model:error="errors.accept" class="text-gray-400">
        Я согласен на
        <RouterLink to="/privacy-policy" class="text-white hover:text-gray-500 underline"
          >обработку персональных данных</RouterLink
        >
      </Checkbox>

      <Checkbox v-model="form.is_escort">Я являюсь представителем</Checkbox>
      <ExpandTransition>
        <div v-if="form.is_escort" class="flex flex-col my-0 mx-auto max-w-xl">
          <h2 class="col-span-2 text-center font-bold text-base md:text-xl my-3">Данные представителя</h2>
          <Input
            v-model="form.escort_last_name"
            v-model:error="errors.escort_last_name"
            placeholder="Фамилия*"
            name="lname"
            autocomplete="family-name"
            required
            class="col-span-2 sm:col-span-1"
          />
          <Input
            v-model="form.escort_first_name"
            v-model:error="errors.escort_first_name"
            placeholder="Имя"
            name="fname"
            autocomplete="given-name"
            class="col-span-2 sm:col-span-1"
          />
          <Input
            v-model="form.escort_middle_name"
            v-model:error="errors.escort_middle_name"
            placeholder="Отчество"
            name="mname"
            autocomplete="additional-name"
            class="col-span-2 sm:col-span-1"
          />
          <DatePicker
            v-model="form.escort_birthday"
            v-model:error="errors.escort_birthday"
            placeholder="Дата рождения*"
            name="bday"
            autocomplete="bday"
            required
            class="col-span-2 sm:col-span-1"
          />
          <PhoneInput
            v-model="form.escort_phone"
            v-model:error="errors.escort_phone"
            :disabled="needConfirm && !!smsTimeRemaining && isEscort"
            name="phone"
            autocomplete="tel"
            required
            class="col-span-2 sm:col-span-1"
          />
          <Input
            v-model="form.escort_email"
            v-model:error="errors.escort_email"
            type="email"
            placeholder="Электронная почта*"
            name="email"
            autocomplete="email"
            required
            class="col-span-2 sm:col-span-1"
          />
        </div>
      </ExpandTransition>
      <YandexRecaptcha v-if="config.CAPTCHA_ENABLED" ref="captcha" :site-key="config.CAPTCHA_SITE_KEY" class="mt-6" />
      <ErrorMessage v-model:error="errors.token" />

      <div class="flex justify-center mt-6">
        <Button type="submit" :loading="load" :disabled="needConfirm">Отправить</Button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref, onActivated } from 'vue'
import { useRouter } from 'vue-router'
import YandexRecaptcha from '@/components/YandexRecaptcha.vue'

import axios from '@/plugins/axios'
import { config } from '@/plugins/config'

import Input from '@/components/Input.vue'
import DatePicker from '@/components/DatePicker.vue'
import PhoneInput from '@/components/PhoneInput.vue'
import Button from '@/components/Button/Button.vue'
import Checkbox from '@/components/Checkbox.vue'
import ErrorMessage from '@/components/ErrorMessage.vue'
import ExpandTransition from '@/components/ExpandTransition.vue'

import { needConfirm, isEscort, smsTimeRemaining, showConfirm } from '@/store/smsAccept'

const router = useRouter()

const form = ref({})
const errors = ref({})
const load = ref()

const captcha = ref()
const captchaToken = ref()

function resetCaptcha() {
  captchaToken.value = undefined
  captcha.value.reset()
}

onActivated(() => {
  captcha.value?.reset()
})

async function submit() {
  if (load.value) {
    return
  }

  load.value = true
  errors.value = {}

  const token = captcha.value?.getToken()

  if (!token) {
    errors.value.token = 'Пройдите капчу'
    load.value = false

    return
  }

  try {
    const {
      data: { confirmed, uuid, is_escort },
    } = await axios.post('send', { ...form.value, token: token })

    if (confirmed) {
      router.push('/registered')
    } else {
      showConfirm(uuid, is_escort, errors)
      router.push('/confirm-phone')
    }
  } catch ({ response }) {
    errors.value = response && response.data ? (response.data.errors ? response.data.errors : {}) : {}

    if (errors.value.token && token) {
      resetCaptcha()
    }
  }

  load.value = false
}
</script>
<style scoped>
h2 {
  font-family: var(--second-family);
}
</style>
